import { supabase } from '../../providers/supabase/client'
import { TourTable } from "../../util/interfaces";

// --- Tour Queries ---

const TOUR_SELECT = `
  id, created_at, name, places_id, kidPrice, adultPrice, operators_id, min_age_kid, max_age_kid, duration, category_id, image_url, gallery_urls, description, operating_days, service_type, itinerary, includes, not_includes, notes,
  places(id, name),
  providers(id, name),
  tour_categories(id, name, icon)
`;

const TOUR_SELECT_FALLBACK = `
  id, created_at, name, places_id, kidPrice, adultPrice, operators_id, min_age_kid, max_age_kid, duration, category_id, image_url, gallery_urls,
  places(id, name),
  providers(id, name),
  tour_categories(id, name, icon)
`;

export const fetchTours = async (): Promise<any> => {
    const res = await supabase
        .from('tours')
        .select(TOUR_SELECT)
        .eq('is_active', true)          // Only return active (non-soft-deleted) tours
        .order('name')
    
    if (!res.error) return res;

    // Fallback: extended fields not available yet (migration pending)
    console.warn("Could not fetch tours with extended fields, falling back to basic fields. Did you run the SQL migration?", res.error);
    return supabase
        .from('tours')
        .select(TOUR_SELECT_FALLBACK)
        .eq('is_active', true)
        .order('name');
}

export const createTour = async (tour: TourTable) => {
    return await supabase
        .from('tours')
        .insert([tour])
        .select(TOUR_SELECT)
}

export const addManyTours = async (tours: TourTable[]) => {
    return await supabase
        .from('tours')
        .insert(tours)
        .select(TOUR_SELECT)
}

export const updateTour = async (id: number, tour: TourTable) => {
    return await supabase
        .from('tours')
        .update(tour)
        .eq('id', id)
        .select(TOUR_SELECT)
}

/** Soft-delete a tour: sets is_active=false. Safe even when the tour has reservations. */
export const deactivateTour = async (id: number) => {
    return await supabase
        .from('tours')
        .update({ is_active: false })
        .eq('id', id)
}

export interface SafeDeleteResult {
    success: boolean;
    blocked?: boolean;
    reservation_count?: number;
    tour_name?: string;
    message?: string;
    error?: string;
    reservations_removed?: number;
}

/**
 * Calls the safe_delete_tour RPC.
 * - force=false (default): returns info if the tour is used by reservations.
 * - force=true: removes reservations_tours references first, then deletes the tour.
 */
export const safeDeleteTour = async (id: number, force = false): Promise<SafeDeleteResult> => {
    const { data, error } = await supabase.rpc('safe_delete_tour', {
        p_tour_id: id,
        p_force: force,
    });
    if (error) return { success: false, error: error.message };
    return data as SafeDeleteResult;
}

// --- Tour Categories ---

export const fetchTourCategories = async () => {
    return await supabase
        .from('tour_categories')
        .select('*')
        .order('name')
}

// --- Tour Image Upload ---

export const uploadTourImage = async (file: File, tourId: number): Promise<string | null> => {
    const fileExt = file.name.split('.').pop()
    const fileName = `${tourId}-${Date.now()}.${fileExt}`
    const filePath = `tours/${fileName}`

    const { error: uploadError } = await supabase.storage
        .from('tour-images')
        .upload(filePath, file, { upsert: true })

    if (uploadError) {
        console.error('Error uploading tour image:', uploadError)
        return null
    }

    const { data } = supabase.storage
        .from('tour-images')
        .getPublicUrl(filePath)

    return data?.publicUrl || null
}
